import { lazy, Suspense, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { IconMap2, IconMoodSad } from '@tabler/icons-react';
import { getMapPoints, type MapPoint } from '../lib/apiClient.ts';
import { MediaViewer } from '../components/media/MediaViewer.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import type { MediaItem } from '../lib/types.ts';
import { kindFromKey } from '../lib/media.ts';
import { eventFromKey } from '../lib/keys.ts';

// Lazy-load leaflet so its bundle is only fetched on the map route.
const PhotoMap = lazy(() =>
  import('../components/views/PhotoMap.tsx').then((m) => ({ default: m.PhotoMap })),
);

/** Turn a server MapPoint into a MediaItem with a required gps field. */
function toGeoItem(p: MapPoint): MediaItem & { gps: { lat: number; lng: number } } {
  return {
    key: p.key,
    thumbnailKey: p.thumbnailKey,
    displayName: p.displayName,
    kind: kindFromKey(p.key),
    event: eventFromKey(p.key),
    timestamp: null,
    gps: p.gps,
  };
}

export function MapPage() {
  // ONE server call returns every accessible geotagged photo. Previously the
  // client fetched N sidecars over N presigned URLs, which timed out on mobile.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['map-points'],
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => getMapPoints(signal),
  });

  const geotagged = useMemo(() => (data?.points ?? []).map(toGeoItem), [data]);
  const [viewerKey, setViewerKey] = useState<string | null>(null);
  const viewerIndex = useMemo(
    () => (viewerKey ? geotagged.findIndex((i) => i.key === viewerKey) : -1),
    [viewerKey, geotagged],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-fg">Map</h1>
        <p className="mt-1 text-sm text-fg-2">
          Photos with location data, plotted on the map.
        </p>
      </div>

      {isLoading && <Skeleton className="h-[70dvh] w-full rounded-3xl" />}

      {isError && <EmptyState icon={IconMoodSad} title="Couldn't load the map" />}

      {!isLoading && !isError && geotagged.length === 0 && (
        <EmptyState
          icon={IconMap2}
          title="No location data yet"
          description="This map plots photos that carry GPS coordinates in their metadata. Currently none of the uploaded photos have location info — either the camera didn't record it, or location services were off when the photos were taken. Newly uploaded photos with GPS turned on will appear here automatically."
        />
      )}

      {!isLoading && geotagged.length > 0 && (
        <>
          <p className="text-xs text-muted">
            Showing {geotagged.length} photo{geotagged.length !== 1 ? 's' : ''} with location data.
            {data?.capped && ` Scanned the first ${data.scanned} photos.`}
          </p>
          <Suspense fallback={<Skeleton className="h-[70dvh] w-full rounded-3xl" />}>
            <PhotoMap items={geotagged} onOpen={setViewerKey} />
          </Suspense>
        </>
      )}

      <AnimatePresence>
        {viewerIndex >= 0 && geotagged[viewerIndex] && (
          <MediaViewer
            items={geotagged}
            index={viewerIndex}
            onIndexChange={(i) => setViewerKey(geotagged[i]?.key ?? null)}
            onClose={() => setViewerKey(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
